/**
 * sheetData — pure helpers for Sort and Find & Replace over a sheet's
 * {cells, formats} maps. Kept out of the React component and unit-tested.
 */
import { cellName, parseRef } from "./sheetEngine";

const cmpVals = (a, b) => {
  const an = Number(a);
  const bn = Number(b);
  const bothNum = a != null && b != null && a !== "" && b !== "" && !Number.isNaN(an) && !Number.isNaN(bn);
  if (bothNum) return an - bn;
  return String(a == null ? "" : a).localeCompare(String(b == null ? "" : b), undefined, { sensitivity: "base" });
};

/**
 * sortRange — reorder the rows of a rectangular range by the values in column
 * `keyCol` (absolute index). Moves both cell contents and formats. Returns new
 * {cells, formats}. Blanks sort last.
 */
export const sortRange = (sheet, range, keyCol, asc = true) => {
  const { r0, c0, r1, c1 } = range;
  const cells = { ...sheet.cells };
  const formats = { ...sheet.formats };
  const rowsArr = [];
  for (let r = r0; r <= r1; r += 1) {
    const cr = [];
    const fr = [];
    for (let c = c0; c <= c1; c += 1) {
      cr.push(sheet.cells[cellName(r, c)]);
      fr.push(sheet.formats[cellName(r, c)]);
      delete cells[cellName(r, c)];
      delete formats[cellName(r, c)];
    }
    rowsArr.push({ cr, fr, key: sheet.cells[cellName(r, keyCol)] });
  }
  rowsArr.sort((a, b) => {
    const aBlank = a.key == null || a.key === "";
    const bBlank = b.key == null || b.key === "";
    if (aBlank && bBlank) return 0;
    if (aBlank) return 1; // blanks always last
    if (bBlank) return -1;
    const r = cmpVals(a.key, b.key);
    return asc ? r : -r;
  });
  rowsArr.forEach((row, i) => {
    const r = r0 + i;
    row.cr.forEach((v, j) => {
      if (v != null && v !== "") cells[cellName(r, c0 + j)] = v;
    });
    row.fr.forEach((f, j) => {
      if (f) formats[cellName(r, c0 + j)] = f;
    });
  });
  return { cells, formats };
};

const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Addresses whose RAW value contains `find` (case-insensitive unless matchCase). */
export const findMatches = (sheet, find, opts = {}) => {
  if (!find) return [];
  const re = new RegExp(escapeRe(find), opts.matchCase ? "" : "i");
  return Object.keys(sheet.cells)
    .filter((addr) => parseRef(addr) && re.test(String(sheet.cells[addr])))
    .sort((a, b) => {
      const pa = parseRef(a);
      const pb = parseRef(b);
      return pa.row - pb.row || pa.col - pb.col;
    });
};

/**
 * removeDuplicates — drop duplicate rows within a range (comparing all columns
 * in the range), keeping the first occurrence, and compact the rest up.
 */
export const removeDuplicates = (sheet, range) => {
  const { r0, c0, r1, c1 } = range;
  const cells = { ...sheet.cells };
  const formats = { ...sheet.formats };
  const seen = new Set();
  const kept = [];
  for (let r = r0; r <= r1; r += 1) {
    const cr = [];
    const fr = [];
    for (let c = c0; c <= c1; c += 1) {
      cr.push(sheet.cells[cellName(r, c)]);
      fr.push(sheet.formats[cellName(r, c)]);
      delete cells[cellName(r, c)];
      delete formats[cellName(r, c)];
    }
    const key = cr.map((v) => (v == null ? "" : String(v))).join("");
    if (seen.has(key)) continue;
    seen.add(key);
    kept.push({ cr, fr });
  }
  kept.forEach((row, i) => {
    const r = r0 + i;
    row.cr.forEach((v, j) => {
      if (v != null && v !== "") cells[cellName(r, c0 + j)] = v;
    });
    row.fr.forEach((f, j) => {
      if (f) formats[cellName(r, c0 + j)] = f;
    });
  });
  return { cells, formats, removed: r1 - r0 + 1 - kept.length };
};

/** textToColumns — split the first column of `range` by `delim` across columns. */
export const textToColumns = (sheet, range, delim = ",") => {
  const { r0, c0, r1 } = range;
  const cells = { ...sheet.cells };
  let maxParts = 1;
  for (let r = r0; r <= r1; r += 1) {
    const v = sheet.cells[cellName(r, c0)];
    if (typeof v === "string" && v.includes(delim)) {
      const parts = v.split(delim);
      maxParts = Math.max(maxParts, parts.length);
      parts.forEach((p, i) => {
        const addr = cellName(r, c0 + i);
        const t = p.trim();
        if (t === "") delete cells[addr];
        else cells[addr] = t;
      });
    }
  }
  return { cells, cols: maxParts };
};

/** Replace all occurrences of `find` with `replace` across raw cell text. */
export const replaceAll = (sheet, find, replace, opts = {}) => {
  if (!find) return { cells: sheet.cells, count: 0 };
  const re = new RegExp(escapeRe(find), opts.matchCase ? "g" : "gi");
  const cells = { ...sheet.cells };
  let count = 0;
  Object.keys(cells).forEach((addr) => {
    const v = cells[addr];
    if (typeof v === "string") {
      const nv = v.replace(re, () => {
        count += 1;
        return replace;
      });
      if (nv !== v) cells[addr] = nv;
    }
  });
  return { cells, count };
};
